'use-client';
import { useScroll } from './Scroll/useScroll';
import React, { useCallback, useEffect, useMemo, useRef } from 'react';
import classNames from 'clsx';
import type { MenuItemProps } from '../../../../../types/vendored/editor-elements-definitions';
import type { IMenuContentProps } from '../../Menu.types';
import { MenuItem } from './MenuItem/MenuItem';
import classes from './style/MenuContent.module.scss';
import { getCurrentMenuItem, isDefaultAnchor } from '../../menuItems';
import { ScrollControls } from './ScrollControls/ScrollControls';
import { scrollToMenuItem, rootLevelMenuItemSelector } from './Scroll/utils';
import { useResizeObserver } from '../../../../../utils/hooks/useResizeObserver';
import { useMenuContext } from '../../contexts/MenuContext';
import { MenuParts } from '../../constants';

const DataHooks = {
  menuRoot: 'menu-root',
};

const MenuContent: React.FC<IMenuContentProps> = props => {
  const {
    id,
    className,
    navbarClassName,
    directionClassName,
    direction,
    submenuDirection,
    onItemMouseIn,
    onItemMouseOut,
    onItemClick,
    onItemDblClick,
    submenuToggleIdPrefix,
    menuElementProps,
    BuilderAnimationIcon,
  } = props;

  const {
    items,
    partToPreviewStateMap,
    currentUrl,
    activeAnchor,
    translations,
    getAnimationPackage,
    navAriaLabel,
    a11yProps,
  } = useMenuContext();

  const rootClassName = classNames(
    classes.root,
    navbarClassName,
    !direction && (directionClassName ?? classes.rootDirection),
  );

  const menuContainerRef = React.useRef<HTMLElement>(null);

  const lastValidAnchorMenuItemRef = useRef<MenuItemProps | undefined>(
    undefined,
  );

  const currentItem = useMemo(() => {
    const menuItem = getCurrentMenuItem(items, activeAnchor, currentUrl);

    if (activeAnchor) {
      if (isDefaultAnchor(activeAnchor)) {
        lastValidAnchorMenuItemRef.current = undefined;
      } else if (menuItem) {
        lastValidAnchorMenuItemRef.current = menuItem;
      } else {
        return lastValidAnchorMenuItemRef.current;
      }
    }

    return menuItem;
  }, [items, activeAnchor, currentUrl]);

  const {
    isScrollable,
    updateScrollState,
    handleOnScroll,
    handleScrollForward,
    handleScrollBackward,
    isScrollBackwardButtonVisible,
    isScrollForwardButtonVisible,
  } = useScroll(menuContainerRef);

  const focusOnMenuItem = useCallback(
    (menuItem: HTMLElement) => {
      const { current: menuContainer } = menuContainerRef;

      if (!isScrollable || !menuItem || !menuContainer) {
        return;
      }
      const rootMenuItem = menuContainer.closest<HTMLElement>(
        rootLevelMenuItemSelector,
      );
      if (rootMenuItem && rootMenuItem !== menuItem) {
        scrollToMenuItem(menuContainer, rootMenuItem);
      } else {
        scrollToMenuItem(menuContainer, menuItem);
      }
    },
    [isScrollable],
  );

  const handleOnFocus = useCallback(
    (event: React.FocusEvent<HTMLElement>) => {
      const targetMenuItem = event.target;
      focusOnMenuItem(targetMenuItem);
    },
    [focusOnMenuItem],
  );

  useResizeObserver({
    ref: menuContainerRef,
    callback: updateScrollState,
  });
  useEffect(updateScrollState, [items, updateScrollState]);

  return (
    <div id={id} className={className}>
      <nav
        className={rootClassName}
        ref={menuContainerRef}
        {...(isScrollable && {
          onScroll: handleOnScroll,
          onFocus: handleOnFocus,
        })}
        data-part={MenuParts.Navbar}
        data-hook={DataHooks.menuRoot}
        {...a11yProps}
        aria-label={navAriaLabel || translations.menuNavAriaLabel}
        dir={direction}
      >
        <ul className={classes.container}>
          {items.map(item => (
            <MenuItem
              translations={translations}
              currentItem={currentItem}
              item={item}
              key={item.id}
              partToPreviewStateMap={partToPreviewStateMap}
              onItemClick={onItemClick}
              focusOnMenuItem={focusOnMenuItem}
              onItemMouseIn={onItemMouseIn}
              onItemMouseOut={onItemMouseOut}
              onItemDblClick={onItemDblClick}
              getAnimationPackage={getAnimationPackage}
              submenuDirection={submenuDirection}
              submenuToggleIdPrefix={submenuToggleIdPrefix}
              menuElementProps={menuElementProps}
              BuilderAnimationIcon={BuilderAnimationIcon}
            />
          ))}
        </ul>
        <ScrollControls
          scrollPageForward={handleScrollForward}
          scrollPageToBackward={handleScrollBackward}
          isScrollBackwardButtonShown={isScrollBackwardButtonVisible}
          isScrollForwardButtonShown={isScrollForwardButtonVisible}
          previewState={partToPreviewStateMap?.['scroll-button']}
          scrollButtonClassName={menuElementProps?.scrollButton?.className}
        />
      </nav>
    </div>
  );
};

export default MenuContent;
